import { CalendarClock, Plus } from 'lucide-react';
import { useState } from 'react';
import { navigate } from '../app/router';
import { GreekText } from '../components/GreekText';
import { WeekDots } from '../components/WeekDots';
import { getItem, reviewCourse } from '../content';
import { yourHabit } from '../lib/habit';
import { planDay } from '../lib/plan';
import { nextReviews } from '../lib/upcoming';
import type { SessionState } from '../state/model';
import { learnEarly, useAppState } from '../state/store';
import { DoneMark } from './LessonDone';

interface Props {
  session: SessionState;
}

/**
 * The end of a session: when to come back first, the week, and (only when no lesson-done screen
 * listed them already) the phrases met today. No confetti.
 */
export function SessionDone({ session }: Props) {
  const state = useAppState();
  const [busy, setBusy] = useState(false);
  const now = new Date();
  const upcoming = nextReviews(state, now);
  const reviewed = Object.keys(session.graded).length;
  const plan = planDay(state, reviewCourse, now);
  const canLearnMore = !plan.courseMet && !plan.braked && plan.newItems.length === 0;

  // A finished lesson has just listed its phrases on its own screen; don't list them twice.
  const listed = session.steps.some((step) => step.kind === 'lesson-done');

  return (
    <div className="step">
      <div className="stack-lg">
        <DoneMark />
        <div className="stack">
          <h2 className="title">That's today's practice</h2>
          <p className="subtitle">
            {session.met.length > 0
              ? `You met ${session.met.length} new ${session.met.length === 1 ? 'phrase' : 'phrases'}${reviewed > session.met.length ? ` and practised ${reviewed} cards` : ''}.`
              : reviewed > 0
                ? `You practised ${reviewed} ${reviewed === 1 ? 'card' : 'cards'}.`
                : 'You played the role-play through.'}
            {session.skipped > 0 && ` ${session.skipped} skipped because the audio wouldn't play.`}
          </p>
        </div>
      </div>

      <div className="panel-quiet row" style={{ alignItems: 'flex-start' }}>
        <CalendarClock size={22} aria-hidden="true" style={{ flex: 'none', color: 'var(--ink-2)', marginTop: 2 }} />
        <div className="stack" style={{ gap: 2 }}>
          <p className="section-title">
            {upcoming ? `Next: ${upcoming.count} ${upcoming.count === 1 ? 'review' : 'reviews'} ${upcoming.when}` : 'Nothing scheduled yet'}
          </p>
          <p className="muted small">
            {state.profile.habit ? `See you then, ${yourHabit(state.profile.habit)}.` : 'Coming back then is what makes it stick.'}
          </p>
        </div>
      </div>

      <WeekDots practiceDays={state.profile.practiceDays} target={state.settings.weeklyTarget} />

      {session.met.length > 0 && !listed && (
        <div className="stack">
          <p className="section-title">You've started on</p>
          <ul className="met-list panel">
            {session.met.map((id) => {
              const item = getItem(id);
              return (
                <li key={id} className="stack" style={{ gap: 2 }}>
                  <GreekText text={item.text} size="body" />
                  <span className="small muted">{item.meaning}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="actions">
        <button type="button" className="btn btn-primary btn-block btn-lg" onClick={() => navigate('/', true)}>
          Done
        </button>
        {canLearnMore && (
          <button
            type="button"
            className="btn btn-ghost btn-block"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              const next = await learnEarly();
              setBusy(false);
              if (!next) navigate('/', true);
            }}
          >
            <Plus size={18} aria-hidden="true" /> Learn the next lesson now
          </button>
        )}
        {canLearnMore && <p className="small muted" style={{ textAlign: 'center' }}>About 10 minutes, plus its reviews over the next days.</p>}
      </div>
    </div>
  );
}
